import React from "react";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface EmptyStateProps {
  isDarkMode: boolean;
  onCreateClick: () => void;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  isDarkMode,
  onCreateClick,
}) => {
  const navigate = useNavigate();
  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7 }}
      className={`flex flex-col items-center justify-center h-screen pb-32 ${
        isDarkMode ? "bg-gray-900" : "bg-gray-100"
      }`}
    >
      <motion.div
        animate={{
          y: [0, -20, 0],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="mb-8"
      >
        <motion.img
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{
            type: "spring",
            stiffness: 100,
            damping: 15,
            delay: 0.2,
          }}
          src="/logo-simple.svg"
          alt="PX Group Logo"
          className={`h-32 w-auto`}
        />
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="text-center"
      >
        <h2
          className={`text-2xl font-bold mb-2 ${
            isDarkMode ? "text-gray-300" : "text-gray-700"
          }`}
        >
          No Active Inspections
        </h2>
        <p
          className={`text-lg mb-8 ${
            isDarkMode ? "text-gray-400" : "text-gray-600"
          }`}
        >
          Create a new inspection to get started
        </p>

        {/* Create Inspection Button */}
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.7 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate("/")}
          className={`px-8 py-4 rounded-xl font-bold text-lg shadow-2xl transition-all duration-300 mr-2 ${
            isDarkMode
              ? "bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-500 hover:to-red-600"
              : "bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700"
          }`}
        >
          <span className="flex items-center gap-3">
            <ArrowLeft className="w-6 h-6" />
            Go dashboard
          </span>
        </motion.button>
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.7 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onCreateClick}
          className={`px-8 py-4 rounded-xl font-bold text-lg shadow-2xl transition-all duration-300 ${
            isDarkMode
              ? "bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-500 hover:to-red-600"
              : "bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700"
          }`}
        >
          <span className="flex items-center gap-3">
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Create New Inspection
          </span>
        </motion.button>
      </motion.div>
    </motion.div>
  );
};
